import React, { useRef, useState } from 'react';
import Image from 'next/image';
import styles from '../../style.module.scss';
import { getProductColorValue } from '@/app/utils';
import { IProduct } from '@mybestbrands/types';
import { trackSSTEvent } from '@/app/utils/tracking'; // Import your tracking function

interface MobileProps {
  images: string[];
  item: IProduct;
}

const MultipleImagesMobile = ({ images, item }: MobileProps) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);
  const [triggeredProducts, setTriggeredProducts] = useState<Set<string>>(new Set()); // Track products that have triggered events

  // Minimum swipe distance to detect a valid swipe
  const minSwipeDistance = 30;

  // Trigger the event if not already triggered for this product
  const triggerTrackingEvent = () => {
    if (!triggeredProducts.has(item.productId)) {
      trackSSTEvent('product_card_multiple_images_swipe', { productId: item.productId });
      setTriggeredProducts((prevSet) => new Set(prevSet).add(item.productId));
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (touchStartX.current && touchEndX.current) {
      const distance = touchStartX.current - touchEndX.current;

      if (distance > minSwipeDistance && currentImageIndex < images.length - 1) {
        // Swipe left (next image)
        setCurrentImageIndex((prevIndex) => prevIndex + 1);
        triggerTrackingEvent(); // Trigger the event
      } else if (distance < -minSwipeDistance && currentImageIndex > 0) {
        // Swipe right (previous image)
        setCurrentImageIndex((prevIndex) => prevIndex - 1);
        triggerTrackingEvent(); // Trigger the event
      }
    }

    // Reset swipe distances
    touchStartX.current = null;
    touchEndX.current = null;
  };

  return (
    <div
      className={styles['sliderContainer']}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}>
      {/* Slider container */}
      <div
        className={styles['imageSlider']}
        style={{
          transform: `translateX(-${currentImageIndex * 100}%)`, // Slide effect
        }}>
        {images.map((image, index) => (
          <div className={styles['sliderImage']} key={index}>
            <Image
              src={image}
              unoptimized={true}
              alt={`${item.brandName} ${item.name} ${getProductColorValue(item.colors)}`}
              width={200}
              height={250}
              loading="lazy"
              className={`${styles['hoveredImage']} ${item.clickOutAllowed ? '' : styles['imgUnavailable']}`}
              onError={(e) => {
                (e.target as HTMLImageElement).src = '/intstatic/images/20x25_white.jpg';
              }}
            />
          </div>
        ))}
      </div>
      {images.length > 1 && (
        <div className={styles['dotContainer']}>
          {images.map((_, index) => (
            <span
              key={index}
              className={`${styles.dot} ${index === currentImageIndex ? styles['activeDot'] : ''}`}></span>
          ))}
        </div>
      )}
    </div>
  );
};

export default MultipleImagesMobile;
